import { Agent } from './agent-types';
import { WidgetOrg } from './org-types';

export enum HideCondition {
  OFF_HOURS = 'off-hours',
  NONE_ONLINE = 'none-online',
  BUSY = 'busy',
}
export interface WidgetSettingsLink {
  url: string;
  title: string;
  icon: string;
}

export enum WidgetButtonType {
  CHAT = 'chat',
  CALL = 'call',
  MEETING = 'meeting',
}

export interface WidgetSettingsOrgButtons {
  elements: {
    name: WidgetButtonType;
    enabled: boolean;
  }[];
  fullWidth: boolean;
}

export enum WidgetSectionType {
  LIVE_AGENTS = 'live-agents',
  HELP_CENTER = 'help-center',
}

export interface WidgetSettingsOrgSections {
  elements: {
    name: WidgetSectionType;
    enabled: boolean;
  }[];
}

export interface WidgetSettingClientRuleArguments {
  cssSelector: string;
  delayMs: number;
  expandOn: 'visit' | 'click' | 'form';
  oncePerSession: boolean;
}

export interface WidgetSettingClientRule {
  arguments: WidgetSettingClientRuleArguments;
  enabledAt: Date;
  id: number;
  operator: 'expand_widget';
}

export interface WidgetAiReplica {
  id: number;
  mediaType: 'video' | 'image';
  thumbnailUrl: string | null;
  xOffset: number | null;
  yOffset: number | null;
}

export interface WidgetAiPersona {
  id: number;
  default: boolean;
  avatarUrl: string | null;
  videoUrl: string | null;
  videoFallbackUrl: string | null;
  role: string | null;
  name: string | null;
  replicas: WidgetAiReplica[];
}

export interface OrgWidgetSettings {
  aiChatEnabled: boolean;
  aiPersonas: WidgetAiPersona[];
  defaultAiPersona?: number | null;
  defaultIntroductionText?: string | null;
  introHandled?: boolean | null;
  conversationStartersEnabled: boolean;
  launcher: 'pill' | 'video' | 'ai';
  hidden: boolean;
  hideConditions?: HideCondition[];
  position: 'right' | 'left';
  answer: 'ask' | 'no-ask';
  mode: 'iframe-immediate' | 'iframe-jit' | 'retrigger' | 'ajax' | 'api';
  copy: Partial<Record<WidgetSettingsCopyKey, string | null>>;
  branding: 'default' | 'none';
  afkTime: number;
  color: null | string;
  bgColor: null | string;
  orgLogoUrl: null | string;
  orgProfileLogoUrl: null | string;
  hasAvatar: boolean;
  hasDarkText: boolean;
  hasLogo: boolean;
  backgroundType: 'audio-video' | 'color' | 'custom-agent-video';
  takeover: 'ask' | 'no-ask';
  viewing: 'ask' | 'no-ask';
  visitorVideo: 'ask' | 'no-ask';
  launcherVideoUrl?: string;
  launcherImageUrl?: string;
  idleCallVideoUrl?: string;
  idleCallImageUrl?: string;
  assetLinking: 'inline' | 'hotlink' | 'proxy';
  showBookingAlways: boolean;
  showChatAlways: boolean;
  disableMobile: boolean;
  links: WidgetSettingsLink[];
  scheduler?: {
    schedulerType:
      | 'servicebell'
      | 'hubspotMeetings'
      | 'calendly'
      | 'acuity'
      | 'chilipiper';
    servicebellEventType?: number;
    servicebellSchedulerUrl?: string;
  };
  requireEmailOnChat: 'away' | 'always' | 'never';
  requireEmailOnChatExcludePersonal: boolean;
  launcherBottomSpacing: number;
  launcherSideSpacing: number;
  buttons: WidgetSettingsOrgButtons;
  sections: WidgetSettingsOrgSections;
  clientRules: WidgetSettingClientRule[];
  setByRules: {
    attribute: string;
    ruleId: number;
    oldValue: unknown;
    newValue: unknown;
    ruleName: string;
    ruleGroup: string;
  }[];
  gdprOptIn?: boolean;
  gdprOptInPrivacyPolicyUrl?: string;
  gdprOptInTitle?: string;
  gdprOptInBody?: string;
  enableLauncherToggle: boolean;
}

export enum WidgetSettingsCopyKey {
  IDLE_HELP_TEXT = 'IDLE_HELP_TEXT',
  IDLE_HELP_TEXT_FOCUSED = 'IDLE_HELP_TEXT_FOCUSED',
  IDLE_BUSY_TEXT = 'IDLE_BUSY_TEXT',
  IDLE_BUSY_TEXT_FOCUSED = 'IDLE_BUSY_TEXT_FOCUSED',
  IDLE_AWAY_TEXT = 'IDLE_AWAY_TEXT',
  IDLE_AWAY_TEXT_FOCUSED = 'IDLE_AWAY_TEXT_FOCUSED',
  ANSWER_CALL = 'ANSWER_CALL',
  NEED_HELP_TITLE = 'NEED_HELP_TITLE',
  NEED_HELP_TEXT = 'NEED_HELP_TEXT',
  NEED_HELP_AWAY_TEXT = 'NEED_HELP_AWAY_TEXT',
  NEED_HELP_AWAY_TITLE = 'NEED_HELP_AWAY_TITLE',
  NEED_HELP_BUSY_TEXT = 'NEED_HELP_BUSY_TEXT',
  NEED_HELP_BUSY_TITLE = 'NEED_HELP_BUSY_TITLE',
  NEED_HELP_TITLE_OFFLINE = 'NEED_HELP_TITLE_OFFLINE',
  NEED_HELP_TEXT_OFFLINE = 'NEED_HELP_TEXT_OFFLINE',
  NEED_HELP_CTA = 'NEED_HELP_CTA',
  DIALING_TITLE = 'DIALING_TITLE',
  RECORDING_OPTIN_TITLE = 'RECORDING_OPTIN_TITLE',
  RECORDING_OPTIN_TEXT = 'RECORDING_OPTIN_TEXT',
}

export interface WidgetInitData {
  commitHash: string;
  protocolVersion: number;
  agents: Agent[];
  allAgents: Agent[];
  org: WidgetOrg;
}
